import React, { FC, useState } from "react";
import { FormikProvider } from "formik";
import { useLocation, useNavigate } from "react-router-dom";

import { useFetchSampleOptions } from "global/hooks/useFetchOptions";

import { useSurveyForm } from "./hooks/useSurveyForm";
import SurveyFormHeader from "./components/SurveyFormHeader";
import SurveyBasicInfoForm from "./components/SurveyBasicInfoForm";
import QuestionsWorkspace from "./components/QuestionsWorkspace";
import SurveyFormFooterActions from "./components/SurveyFormFooterActions";
import { SelectQuestionItem, SurveyFormProps } from "./types";
import QuestionBankForm from "../../question-bank copy/form/question-bank-form";

const SurveyForm: FC<SurveyFormProps> = ({ setOpen, refreshTable }) => {
  const location = useLocation();
  const currentData = location.state?.currentData || null;

  const [modals, setModals] = useState({
    questionBank: { open: false, currentData: null as any },
  });

  const {
    formik,
    questionLoading,
    dependencyState,
    selectedQuestions,
    availableQuestions,
    addQuestionToSelected,
    handleDependencyChange,
    handleRequiredChange,
    handleDeleteQuestion,
    handleDeleteAll,
    onDragEnd,
  } = useSurveyForm({
    currentData,
    questionBankOpen: modals.questionBank.open,
    setOpen,
    refreshTable,
  });

  const { values, setFieldValue, touched, errors } = formik;
  const navigate = useNavigate();
  const { dataOptions: replyTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/replyTypeSelect"
  );
  const { dataOptions: questionTypeSelect } = useFetchSampleOptions(
    "/customer-api/question/questionTypeSelect"
  );
  const { dataOptions: questionAssessmentMethodSelect } = useFetchSampleOptions(
    "/customer-api/question/questionAssessmentMethodSelect"
  );

  const openQuestionBankModal = () =>
    setModals((prev) => ({
      ...prev,
      questionBank: { open: true, currentData: null },
    }));

  const closeQuestionBankModal = () =>
    setModals({
      ...modals,
      questionBank: { open: false, currentData: "" },
    });

  return (
    <>
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          className="w-full min-h-screen bg-[#f8f9fb]"
          dir="rtl"
        >
          <div className="max-w-[1600px] mx-auto px-4 md:px-6 xl:px-8 py-6">
            <SurveyFormHeader
              isEditMode={Boolean(currentData)}
              selectedQuestionsCount={(values.selectQuestionList || []).length}
              isActive={values.isActive}
              onActiveChange={(v) => setFieldValue("isActive", v)}
            />

            <SurveyBasicInfoForm
              values={values}
              touched={touched}
              errors={errors}
              setFieldValue={setFieldValue}
              questionTypeOptions={questionAssessmentMethodSelect?.options}
            />

            <QuestionsWorkspace
              onDragEnd={onDragEnd}
              questionLoading={questionLoading}
              availableQuestions={availableQuestions}
              selectedQuestionIds={(values.selectQuestionList || []).map(
                (item: SelectQuestionItem) => item.questionId
              )}
              onAddQuestion={addQuestionToSelected}
              onOpenQuestionBank={openQuestionBankModal}
              selectedQuestions={selectedQuestions}
              dependencyState={dependencyState}
              onDependencyChange={handleDependencyChange}
              onRequiredChange={handleRequiredChange}
              onDeleteQuestion={handleDeleteQuestion}
              onDeleteAll={handleDeleteAll}
            />

            <SurveyFormFooterActions
              isEditMode={Boolean(currentData)}
              onCancel={() => navigate("/basic-information/survey")}
            />
          </div>
        </form>
      </FormikProvider>

      {modals.questionBank.open && (
        <QuestionBankForm
          options={{
            replyTypeSelect: replyTypeSelect.options,
            questionTypeSelect:questionTypeSelect?.options ,
            questionAssessmentMethodSelect:questionAssessmentMethodSelect?.options ,
          }}
          currentData={modals.questionBank.currentData}
          open={modals.questionBank.open}
          setOpen={closeQuestionBankModal}
        />
      )}
    </>
  );
};

export default SurveyForm;
